import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { BrowserProvider, Contract, formatEther, formatUnits } from "ethers";
import { CHAIN_ID, NET, STAMP_TOKEN, TOKEN_CONFIGURED, ERC20_ABI } from "../config";
import { toast } from "sonner";

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [balance, setBalance] = useState(0);      // STAMP (human)
  const [bnb, setBnb] = useState(0);
  const [decimals, setDecimals] = useState(18);
  const [chainOk, setChainOk] = useState(true);

  const getProvider = useCallback(() => {
    if (typeof window === "undefined" || !window.ethereum) return null;
    return new BrowserProvider(window.ethereum);
  }, []);

  const readBalances = useCallback(async (provider, addr) => {
    try {
      const b = await provider.getBalance(addr);
      setBnb(Number(formatEther(b)));
    } catch (e) { /* ignore */ }
    if (TOKEN_CONFIGURED) {
      try {
        const token = new Contract(STAMP_TOKEN, ERC20_ABI, provider);
        const dec = await token.decimals();
        setDecimals(Number(dec));
        const raw = await token.balanceOf(addr);
        setBalance(Number(formatUnits(raw, dec)));
      } catch (e) {
        setBalance(0);
      }
    }
  }, []);

  const ensureChain = useCallback(async () => {
    if (!window.ethereum) return false;
    try {
      const current = await window.ethereum.request({ method: "eth_chainId" });
      if (parseInt(current, 16) === CHAIN_ID) { setChainOk(true); return true; }
      try {
        await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: NET.chainIdHex }] });
        setChainOk(true); return true;
      } catch (err) {
        if (err && err.code === 4902) {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{ chainId: NET.chainIdHex, chainName: NET.name, nativeCurrency: { name: NET.symbol, symbol: NET.symbol, decimals: 18 }, rpcUrls: NET.rpc, blockExplorerUrls: [NET.explorer] }],
          });
          setChainOk(true); return true;
        }
        setChainOk(false); return false;
      }
    } catch (e) { setChainOk(false); return false; }
  }, []);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      toast.error("No wallet detected", { description: "Install MetaMask to connect to BNB Chain." });
      return null;
    }
    setConnecting(true);
    try {
      const provider = getProvider();
      await provider.send("eth_requestAccounts", []);
      await ensureChain();
      const signer = await provider.getSigner();
      const addr = await signer.getAddress();
      setAddress(addr);
      await readBalances(provider, addr);
      return addr;
    } catch (e) {
      toast.error("Connection failed", { description: e?.shortMessage || e?.message || "Rejected" });
      return null;
    } finally {
      setConnecting(false);
    }
  }, [getProvider, ensureChain, readBalances]);

  const disconnect = useCallback(() => {
    setAddress(null); setBalance(0); setBnb(0);
  }, []);

  const refresh = useCallback(async () => {
    const provider = getProvider();
    if (provider && address) await readBalances(provider, address);
  }, [getProvider, address, readBalances]);

  useEffect(() => {
    if (!window.ethereum) return;
    const onAccounts = (accts) => { if (!accts || accts.length === 0) disconnect(); else setAddress(accts[0]); };
    const onChain = () => window.location.reload();
    window.ethereum.on?.("accountsChanged", onAccounts);
    window.ethereum.on?.("chainChanged", onChain);
    return () => {
      window.ethereum.removeListener?.("accountsChanged", onAccounts);
      window.ethereum.removeListener?.("chainChanged", onChain);
    };
  }, [disconnect]);

  const short = address ? `${address.slice(0, 6)}\u2026${address.slice(-4)}` : null;

  return (
    <WalletContext.Provider value={{ address, short, connecting, balance, setBalance, bnb, decimals, chainOk, connect, disconnect, refresh, getProvider }}>
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext);
